import React, { useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { formatDateISO } from '../../../utils/dateUtils';
import { FlightDetailsDialog, fetchFlightDetails, type Flight } from '../../flight-details';
import { useTranslation } from '../../../i18n';
import { flightFileLabel } from '../../../utils/flightFileRefs';
import { useLatestFlightsQuery, fetchLatestFlightsPage, dashboardKeys } from './dashboard.queries';
import { useQueryClient } from '@tanstack/react-query';

interface LatestFlight {
  id: string;
  drone_serial: string;
  flight_date: string;
  flight_hours: number | null;
  s3_key: string | null;
  /**
   * The uploaded log's original filename, from the flight row (#39 Phase B2).
   * Null for legacy rows that predate the column — see `extractFilename`.
   */
  log_basename: string | null;
}

function formatFlightDuration(hours: number | null): string {
  if (hours === null || hours === undefined) return 'N/A';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
}

// The name comes from the ROW, via the shared pure helper in
// `utils/flightFileRefs` — extracted there so it can be tested at all (no jsdom
// in this app, and `tsc` cannot tell `log_basename` from a key slice: both are
// `string`). See that module for why the key arm stays as the legacy exemption.

const StyledFlightCard = styled(Box)({
  padding: '12px',
  marginBottom: '8px',
  border: '1px solid #e0e0e0',
  borderLeft: `3px solid ${BRAND_COLORS.forestGreen.main}`,
  borderRadius: '8px',
  backgroundColor: '#fafafa',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    transform: 'translateY(-1px)',
    boxShadow: `0 2px 8px ${alpha(BRAND_COLORS.forestGreen.main, 0.12)}`,
  },
});

const PAGE_SIZE = 5;

export const LatestFlightsSection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Accumulated list of fetched flights across all pages
  const [allFlights, setAllFlights] = useState<LatestFlight[]>([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Flight detail dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFlightDate, setSelectedFlightDate] = useState<string | null>(null);
  // The dialog's own `Flight`, not a structural copy of it. The copy that lived
  // here had to be edited in lockstep with the shared type, and the two fields
  // #1294 added — `object_id` and `log_basename` — are exactly what a copy
  // silently omits: the dialog would then be handed rows that cannot name or
  // address their own file, and the Plot button would stay missing here while
  // working everywhere else.
  const [flightDetails, setFlightDetails] = useState<Flight[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedDroneSerial, setSelectedDroneSerial] = useState<string | null>(null);
  const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);

  // Initial page query
  const { data: firstPage, isLoading: loading, isError } = useLatestFlightsQuery(PAGE_SIZE, 0);

  // Merge first page into allFlights on first load
  React.useEffect(() => {
    if (firstPage && allFlights.length === 0) {
      setAllFlights(firstPage);
      setHasMore(firstPage.length === PAGE_SIZE);
    }
  }, [firstPage, allFlights.length]);

  const handleShowMore = async () => {
    const nextOffset = currentOffset + PAGE_SIZE;
    setLoadingMore(true);
    try {
      // Fetch the next page directly via queryClient.fetchQuery so we can
      // append the results manually (avoids replacing the displayed list).
      const nextPage = await queryClient.fetchQuery({
        queryKey: dashboardKeys.latestFlights(PAGE_SIZE, nextOffset),
        queryFn: () => fetchLatestFlightsPage(PAGE_SIZE, nextOffset),
      });
      setAllFlights((prev) => [...prev, ...nextPage]);
      setHasMore(nextPage.length === PAGE_SIZE);
      setCurrentOffset(nextOffset);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFlightClick = async (flight: LatestFlight) => {
    const flightDate = formatDateISO(flight.flight_date);
    setSelectedFlightDate(flightDate);
    setSelectedDroneSerial(flight.drone_serial);
    setSelectedFlightId(flight.id);
    setDialogOpen(true);
    setDetailsLoading(true);

    try {
      const response = await fetchFlightDetails(flight.drone_serial, flightDate);
      if (response.success) {
        const matched = response.flights.filter((f) => f.flight_id === flight.id);
        setFlightDetails(matched.length > 0 ? matched : response.flights);
      } else {
        setFlightDetails([]);
      }
    } catch (err) {
      console.error('Error fetching flight details:', err);
      setFlightDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedFlightDate(null);
    setSelectedDroneSerial(null);
    setSelectedFlightId(null);
    setFlightDetails([]);
  };

  const handleFlightUpdated = async () => {
    if (selectedDroneSerial && selectedFlightDate) {
      try {
        const response = await fetchFlightDetails(selectedDroneSerial, selectedFlightDate);
        if (response.success) {
          if (selectedFlightId) {
            const matched = response.flights.filter((f) => f.flight_id === selectedFlightId);
            setFlightDetails(matched.length > 0 ? matched : response.flights);
          } else {
            setFlightDetails(response.flights);
          }
        }
      } catch (err) {
        console.error('Error refreshing flight details:', err);
      }
    }
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Typography variant="h5" sx={{ color: BRAND_COLORS.forestGreen.main, mb: 2 }}>
        {t('overview.latestFlights')}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Typography variant="body2" color="error" sx={{ py: 2 }}>
          {t('overview.failedToLoadFlights')}
        </Typography>
      )}

      {!loading && !isError && allFlights.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {t('overview.noFlightsRecorded')}
          </Typography>
        </Box>
      )}

      {!loading && allFlights.length > 0 && (
        <>
          {allFlights.map((flight) => (
            <StyledFlightCard key={flight.id} onClick={() => handleFlightClick(flight)}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: BRAND_COLORS.forestGreen.main }}
                >
                  SN{flight.drone_serial}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateISO(flight.flight_date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {formatFlightDuration(flight.flight_hours)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {flightFileLabel(flight)}
                </Typography>
              </Box>
            </StyledFlightCard>
          ))}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                variant="outlined"
                onClick={handleShowMore}
                disabled={loadingMore}
                sx={{
                  borderColor: BRAND_COLORS.forestGreen.main,
                  color: BRAND_COLORS.forestGreen.main,
                  '&:hover': {
                    borderColor: BRAND_COLORS.forestGreen.main,
                    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
                  },
                }}
              >
                {loadingMore ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                {loadingMore ? 'Loading...' : 'Show more'}
              </Button>
            </Box>
          )}
        </>
      )}

      <FlightDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        flightDate={selectedFlightDate}
        droneSerial={selectedDroneSerial ?? ''}
        flights={flightDetails}
        loading={detailsLoading}
        onFlightUpdated={handleFlightUpdated}
      />
    </Box>
  );
};
